import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Pagination } from '../components/ui/pagination';
import { formatDate } from '../lib/utils';

const PAGE_SIZE = 20;

export function AppsPage() {
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; appId: string; appName: string }>({
    show: false,
    appId: '',
    appName: '',
  });
  const [newAppTitle, setNewAppTitle] = useState('');
  const [newAppDefinitionId, setNewAppDefinitionId] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['apps', page],
    queryFn: () =>
      api
        .get('/api/apps/list', { params: { offset: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE } })
        .then((r) => r.data),
  });

  const { data: appDefinitionsData } = useQuery({
    queryKey: ['app-definitions'],
    queryFn: () => api.get('/api/apps/definition/list').then((r) => r.data),
  });

  const apps = data?.query?.data ?? [];
  const total = data?.query?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const appDefinitions = Array.isArray(appDefinitionsData)
    ? appDefinitionsData
    : (appDefinitionsData?.query?.data ?? []);

  async function handleAddApp(e: React.FormEvent) {
    e.preventDefault();
    if (!newAppTitle || !newAppDefinitionId) return;
    try {
      await api.post('/api/apps/add', {
        title: newAppTitle,
        appDefinitionId: newAppDefinitionId,
      });
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      setShowAddModal(false);
      setNewAppTitle('');
      setNewAppDefinitionId('');
    } catch (err) {
      console.error('Failed to add app:', err);
    }
  }

  async function handleDeleteApp(appInstanceId: string) {
    try {
      await api.post('/api/apps/delete_app', { appInstanceId });
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      setDeleteModal({ show: false, appId: '', appName: '' });
    } catch (err) {
      console.error('Failed to delete app:', err);
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-3">Apps</h1>

      <div className="flex items-center justify-between mb-3">
        <p className="font-semibold text-[#767676]">
          To add a new app, click "Register New App" to submit an OAuth request and set up your app.
        </p>
        <Button variant="secondary" onClick={() => setShowAddModal(true)}>
          Register New App
        </Button>
      </div>

      {/* Empty state */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading apps...</div>
      ) : total === 0 ? (
        <Card className="rounded-[20px]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">You have no active apps!</h2>
              <img src="/assets/icon-action-graph.svg" alt="" className="w-16 h-16" />
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-[20px] mb-5">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-3">
              <h5 className="text-base font-semibold">My Apps</h5>
            </div>

            {/* Table header */}
            <div className="grid grid-cols-12 gap-2 text-sm font-semibold text-[#767676] border-b pb-2 mb-2">
              <div className="col-span-1">&nbsp;</div>
              <div className="col-span-5">App Name</div>
              <div className="col-span-2">Created</div>
              <div className="col-span-2">Modified</div>
              <div className="col-span-2">Actions</div>
            </div>

            {/* Table rows */}
            {apps.map((app: any, index: number) => (
              <div key={app.app_instance_id}>
                <div className="grid grid-cols-12 gap-2 items-center py-4">
                  <div className="col-span-1">
                    <img
                      src={app.app_logo || '/assets/images/default/organization.png'}
                      alt={app.app_name}
                      width={36}
                      height={36}
                      className="rounded object-contain"
                    />
                  </div>
                  <div className="col-span-5">
                    <Link
                      to={`/apps/details?id=${app.app_instance_id}`}
                      className="hover:underline"
                    >
                      {app.app_instance_name}
                    </Link>
                  </div>
                  <div className="col-span-2 text-sm text-[#767676]">
                    {formatDate(app.created)}
                  </div>
                  <div className="col-span-2 text-sm text-[#767676]">
                    {formatDate(app.modified)}
                  </div>
                  <div className="col-span-2 flex gap-2">
                    <Link
                      to={`/apps/details?id=${app.app_instance_id}`}
                      className="text-[#767676] hover:text-[#cc0404]"
                      title="Edit"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                      </svg>
                    </Link>
                    <button
                      className="text-[#767676] hover:text-[#cc0404]"
                      onClick={() => setDeleteModal({
                        show: true,
                        appId: app.app_instance_id,
                        appName: app.app_instance_name,
                      })}
                      title="Delete"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                      </svg>
                    </button>
                  </div>
                </div>
                {index < apps.length - 1 && (
                  <hr className="border-t border-gray-200 opacity-75" />
                )}
              </div>
            ))}

            {totalPages > 1 && (
              <div className="mt-4">
                <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Register New App modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-[20px] max-w-lg w-full mx-4 shadow-xl">
            <div className="bg-gray-50 px-6 py-4 rounded-t-[20px] border-b">
              <h5 className="text-lg font-semibold text-[#cc0404]">Register New App</h5>
            </div>
            <form onSubmit={handleAddApp} className="p-6">
              <h6 className="text-sm font-semibold text-[#cc0404] mb-4">App Information</h6>
              <div className="mb-3">
                <label className="block text-sm font-semibold mb-1">Title</label>
                <input
                  type="text"
                  className="w-full h-[42px] rounded-[4px] border border-gray-300 px-3 text-sm"
                  placeholder="Enter app title"
                  value={newAppTitle}
                  onChange={(e) => setNewAppTitle(e.target.value)}
                  required
                  maxLength={512}
                />
              </div>
              <div className="mb-3">
                <label className="block text-sm font-semibold mb-1">App Definition</label>
                <select
                  className="w-full h-[42px] rounded-[4px] border border-gray-300 px-3 text-sm"
                  value={newAppDefinitionId}
                  onChange={(e) => setNewAppDefinitionId(e.target.value)}
                  required
                >
                  <option value="">Select App</option>
                  {appDefinitions.map((def: any) => (
                    <option key={def.app_definition_id} value={def.app_definition_id}>
                      {def.app_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => { setShowAddModal(false); setNewAppTitle(''); setNewAppDefinitionId(''); }}
                >
                  Cancel
                </Button>
                <Button type="submit">Register</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-[20px] p-6 max-w-md w-full mx-4 shadow-xl">
            <h5 className="text-lg font-semibold mb-4">Delete Confirmation</h5>
            <p className="mb-2">Are you sure you want to delete this app?</p>
            <p className="text-sm text-[#767676] mb-6">
              <b>Note:</b> Deleted app can not be restored.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setDeleteModal({ show: false, appId: '', appName: '' })}>
                Close
              </Button>
              <Button variant="destructive" onClick={() => handleDeleteApp(deleteModal.appId)}>
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}